/**
 * PostHog analytics initialization and event tracking.
 *
 * Wraps posthog-js with safe guards:
 * - Only initializes when VITE_POSTHOG_KEY is set
 * - Gracefully no-ops in dev without key
 * - Identifies user on auth, resets on logout
 */

import posthog from "posthog-js";
import { log } from './logger';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string) || "https://us.i.posthog.com";

let _initialized = false;

/** Initialize PostHog. Call once at app root after auth is ready. */
export function initPostHog(): void {
  if (_initialized) return;
  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      log.debug("POSTHOG", "No VITE_POSTHOG_KEY — analytics disabled");
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false, // manual events only for now
    capture_pageview: true,
    capture_pageleave: true,
    persistence: "localStorage",
    disable_session_recording: import.meta.env.DEV, // enable recordings in prod only
  });

  _initialized = true;
}

/** Identify the authenticated user. Call on login / auth ready. */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  if (!_initialized) return;
  posthog.identify(userId, traits);
}

/** Reset identity on logout. */
export function resetUser(): void {
  if (!_initialized) return;
  posthog.reset();
}

/**
 * Capture a product analytics event.
 * No-ops gracefully when PostHog is not initialized.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    log.debug("POSTHOG", event, properties);
  }
  if (!_initialized) return;
  posthog.capture(event, properties);
}

// ─── Core funnel events ─────────────────────────────────────────────────────

export function trackSignupStarted(method: "email" | "google" | "apple" | "strava"): void {
  capture("signup_started", { method });
}

export function trackSignupCompleted(method: "email" | "google" | "apple"): void {
  capture("signup_completed", { method });
}

export function trackOnboardingCompleted(profile: {
  sports: string[];
  goal: string;
  days_per_week: number;
  device_connected: boolean;
}): void {
  capture("onboarding_completed", profile);
}

export function trackFirstActivitySynced(provider: "strava" | "garmin"): void {
  capture("first_activity_synced", { provider });
}

export function trackFirstCoachChat(): void {
  capture("first_coach_chat");
}

export function trackFirstPlanGenerated(plan: {
  weeks: number;
  sessions: number;
  goal_type?: string;
}): void {
  capture("first_plan_generated", plan);
}

// ─── Quick onboarding v2 funnel events ──────────────────────────────────────

export function trackOnboardingStepViewed(step: string, stepNumber: number): void {
  capture("onboarding_step_viewed", { step, step_number: stepNumber });
}

export function trackOnboardingStepCompleted(step: string, stepNumber: number, value: string, timeOnStepMs: number): void {
  capture("onboarding_step_completed", { step, step_number: stepNumber, value, time_on_step_ms: timeOnStepMs });
}

export function trackOnboardingPlanGenerating(data: { sport: string; goal: string; days: number }): void {
  capture("onboarding_plan_generating", data);
}

export function trackOnboardingPlanReady(data: { duration_ms: number; provisional: boolean; sessions_count: number }): void {
  capture("onboarding_plan_ready", data);
}

export function trackOnboardingCompletedQuick(data: { total_time_ms: number; sport: string; goal: string; days: number }): void {
  capture("onboarding_completed_quick", data);
}

// ─── Post-onboarding activation events ──────────────────────────────────────

export function trackActivationDevicePromptShown(dayNumber: number): void {
  capture("activation_device_prompt_shown", { day_number: dayNumber });
}

export function trackActivationDevicePromptClicked(provider: string): void {
  capture("activation_device_prompt_clicked", { provider });
}

export function trackActivationDevicePromptDismissed(dismissCount: number): void {
  capture("activation_device_prompt_dismissed", { dismiss_count: dismissCount });
}

export function trackActivationDeviceConnected(provider: string, daysSinceSignup: number): void {
  capture("activation_device_connected", { provider, days_since_signup: daysSinceSignup });
}

// ─── App Store CTA events ────────────────────────────────────────────────────

export function trackAppStoreClick(placement: "navbar" | "hero" | "footer"): void {
  capture("app_store_click", { placement });
}
